import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Globe, 
  CheckCircle2, 
  AlertCircle, 
  Languages,
  FileText,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Download,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';

// Define all translatable pages/components and their translation keys
const translationRegistry = {
  // Core Pages
  landing: {
    name: 'Landing Page',
    keys: ['landing.hero.title', 'landing.hero.subtitle', 'landing.features.title', 'landing.cta.getStarted'],
    category: 'core'
  },
  navbar: {
    name: 'Navigation Bar',
    keys: ['nav.home', 'nav.chat', 'nav.symptoms', 'nav.resources', 'nav.messages', 'nav.profile'],
    category: 'core'
  },
  login: {
    name: 'Login Page',
    keys: ['auth.signIn', 'auth.signUp', 'auth.email', 'auth.password', 'auth.welcomeBack'],
    category: 'core'
  },
  contact: {
    name: 'Contact Page',
    keys: ['contact.title', 'contact.sendMessage', 'contact.fullName', 'contact.email', 'contact.subject'],
    category: 'core'
  },
  
  // Dashboard Pages
  careDashboard: {
    name: 'Care Dashboard',
    keys: ['dashboard.greeting.morning', 'dashboard.checkIn', 'dashboard.medications', 'dashboard.quickActions'],
    category: 'dashboard'
  },
  patientDashboard: {
    name: 'Patient Dashboard',
    keys: ['patientDashboard.overview', 'patientDashboard.visits', 'patientDashboard.medications', 'patientDashboard.familyPortal'],
    category: 'dashboard'
  },
  staffPortal: {
    name: 'Staff Portal',
    keys: ['staffPortal.title', 'staffPortal.tabs.caseload', 'staffPortal.tabs.messages', 'staffPortal.stats.myPatients'],
    category: 'dashboard'
  },
  familyPortal: {
    name: 'Family Portal',
    keys: ['familyPortal.title', 'familyPortal.careTimeline', 'familyPortal.yourCareTeam', 'familyPortal.upcomingVisits'],
    category: 'dashboard'
  },
  
  // Feature Pages
  chat: {
    name: 'AI Chat',
    keys: ['chat.topics', 'chat.placeholder', 'chat.disclaimer', 'chat.recording', 'chat.ttsOn'],
    category: 'features'
  },
  messages: {
    name: 'Messages',
    keys: ['messages.title', 'messages.newConversation', 'messages.typeMessage', 'messages.sendMessage'],
    category: 'features'
  },
  medications: {
    name: 'Medications',
    keys: ['medications.title', 'medications.addMedication', 'medications.schedule', 'medications.dosage'],
    category: 'features'
  },
  symptoms: {
    name: 'Symptom Guide',
    keys: ['symptomGuide.title', 'symptomGuide.search', 'symptomGuide.comfortMeasures', 'symptomGuide.whenToCall'],
    category: 'features'
  },
  resources: {
    name: 'Resources',
    keys: ['resources.title', 'resources.searchPlaceholder', 'resources.allResources', 'resources.needHelp'],
    category: 'features'
  },
  calendar: {
    name: 'Care Calendar',
    keys: ['calendar.title', 'calendar.addEvent', 'calendar.upcoming', 'calendar.appointment'],
    category: 'features'
  },
  journal: {
    name: 'Caregiver Journal',
    keys: ['journal.title', 'journal.mood', 'journal.symptoms', 'journal.notes'],
    category: 'features'
  },
  profile: {
    name: 'Profile Page',
    keys: ['profile.title', 'profile.personalInfo', 'profile.security', 'profile.notifications'],
    category: 'features'
  },
  
  // Staff Pages
  staffRoster: {
    name: 'Staff Roster',
    keys: ['staffRoster.title', 'staffRoster.totalStaff', 'staffRoster.searchPlaceholder', 'staffRoster.allRoles'],
    category: 'staff'
  },
  
  // Common/Shared
  common: {
    name: 'Common Elements',
    keys: ['common.save', 'common.cancel', 'common.delete', 'common.loading', 'common.returnHome'],
    category: 'common'
  },
  languageTour: {
    name: 'Language Tour',
    keys: ['languageTour.title', 'languageTour.welcome', 'languageTour.keyPhrases', 'languageTour.gotIt'],
    category: 'common'
  }
};

const languageInfo = {
  en: { name: 'English', flag: '🇺🇸', rtl: false },
  es: { name: 'Español', flag: '🇪🇸', rtl: false },
  fr: { name: 'Français', flag: '🇫🇷', rtl: false },
  zh: { name: '中文', flag: '🇨🇳', rtl: false },
  vi: { name: 'Tiếng Việt', flag: '🇻🇳', rtl: false },
  ko: { name: '한국어', flag: '🇰🇷', rtl: false },
  tl: { name: 'Tagalog', flag: '🇵🇭', rtl: false },
  ar: { name: 'العربية', flag: '🇸🇦', rtl: true }
};

const categories = {
  core: { name: 'Core Pages', icon: FileText },
  dashboard: { name: 'Dashboards', icon: BarChart3 },
  features: { name: 'Features', icon: Languages },
  staff: { name: 'Staff Pages', icon: FileText },
  common: { name: 'Common Elements', icon: Globe }
};

export default function TranslationDashboard() {
  const { t, i18n } = useTranslation();
  const [expandedCategories, setExpandedCategories] = useState(['core']);
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);

  // Calculate translation coverage for each page and language
  const translationStats = useMemo(() => {
    const stats = {};
    const languages = Object.keys(languageInfo);
    
    languages.forEach(lang => {
      stats[lang] = {
        total: 0,
        translated: 0,
        pages: {}
      };
      
      Object.entries(translationRegistry).forEach(([pageId, page]) => {
        const pageStats = {
          total: page.keys.length,
          translated: 0,
          missing: []
        };
        
        page.keys.forEach(key => {
          stats[lang].total++;
          // Check if translation exists (not same as key and not empty)
          const translation = i18n.getResource(lang, 'translation', key);
          if (translation && translation !== key) {
            pageStats.translated++;
            stats[lang].translated++;
          } else {
            pageStats.missing.push(key);
          }
        });
        
        stats[lang].pages[pageId] = pageStats;
      });
    });
    
    return stats;
  }, [i18n]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'bg-green-500';
    if (percentage >= 70) return 'bg-yellow-500';
    if (percentage >= 50) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const overallPercentage = (lang) => {
    const stats = translationStats[lang];
    return stats ? Math.round((stats.translated / stats.total) * 100) : 0;
  };

  // Get all missing keys for a language
  const getMissingKeys = (lang) => {
    const missing = [];
    const stats = translationStats[lang];
    if (!stats) return missing;
    
    Object.entries(stats.pages).forEach(([pageId, pageStats]) => {
      const page = translationRegistry[pageId];
      pageStats.missing.forEach(key => {
        // Get the English translation as reference
        const englishValue = i18n.getResource('en', 'translation', key) || '';
        missing.push({
          page: page?.name || pageId,
          category: page?.category || 'unknown',
          key,
          englishValue
        });
      });
    });
    
    return missing;
  };

  // Export missing keys as JSON
  const exportAsJSON = () => {
    const missingKeys = getMissingKeys(selectedLanguage);
    const exportData = {
      language: selectedLanguage,
      languageName: languageInfo[selectedLanguage]?.name,
      exportDate: new Date().toISOString(),
      totalMissing: missingKeys.length,
      keys: missingKeys
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `missing-translations-${selectedLanguage}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success(t('translationDashboard.exportSuccess', 'Missing keys exported successfully'));
  };

  // Export missing keys as CSV
  const exportAsCSV = () => {
    const missingKeys = getMissingKeys(selectedLanguage);
    const headers = ['Page', 'Category', 'Key', 'English Value', 'Translation'];
    const rows = missingKeys.map(item => [
      item.page,
      item.category,
      item.key,
      `"${(item.englishValue || '').replace(/"/g, '""')}"`,
      '' // Empty column for translator to fill
    ]);
    
    const csv = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `missing-translations-${selectedLanguage}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success(t('translationDashboard.exportSuccess', 'Missing keys exported successfully'));
  };

  // Copy missing keys to clipboard
  const copyToClipboard = () => {
    const missingKeys = getMissingKeys(selectedLanguage);
    const text = missingKeys.map(item => 
      `"${item.key}": "${item.englishValue || ''}",`
    ).join('\n');
    
    navigator.clipboard.writeText(text).then(() => {
      toast.success(t('translationDashboard.copiedToClipboard', 'Missing keys copied to clipboard'));
    }).catch(() => {
      toast.error(t('translationDashboard.copyFailed', 'Failed to copy to clipboard'));
    });
  };

  return (
    <div className="min-h-screen bg-muted/30 p-6" data-testid="translation-dashboard">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <Globe className="w-8 h-8 text-primary" />
            {t('translationDashboard.title', 'Translation Progress Dashboard')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {t('translationDashboard.subtitle', 'Track internationalization coverage across all pages and languages')}
          </p>
        </div>

        {/* Language Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {Object.entries(languageInfo).map(([code, info]) => {
            const percentage = overallPercentage(code);
            return (
              <Card 
                key={code}
                className={`cursor-pointer transition-all ${selectedLanguage === code ? 'ring-2 ring-primary' : 'hover:shadow-md'}`}
                onClick={() => setSelectedLanguage(code)}
                data-testid={`lang-card-${code}`}
              >
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-2xl">{info.flag}</span>
                    <Badge variant={percentage >= 90 ? 'default' : percentage >= 70 ? 'secondary' : 'destructive'}>
                      {percentage}%
                    </Badge>
                  </div>
                  <p className="font-medium text-sm">{info.name}</p>
                  <Progress value={percentage} className="h-2 mt-2" />
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Detailed View */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-2xl">{languageInfo[selectedLanguage]?.flag}</span>
                  {languageInfo[selectedLanguage]?.name} {t('translationDashboard.coverage', 'Coverage')}
                </CardTitle>
                <CardDescription>
                  {translationStats[selectedLanguage]?.translated} / {translationStats[selectedLanguage]?.total} {t('translationDashboard.keysTranslated', 'keys translated')}
                </CardDescription>
              </div>
              {/* Export Buttons */}
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={copyToClipboard}
                  data-testid="copy-missing-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  {t('translationDashboard.copyMissing', 'Copy Missing')}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={exportAsCSV}
                  data-testid="export-csv-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  CSV
                </Button>
                <Button 
                  variant="default" 
                  size="sm" 
                  onClick={exportAsJSON}
                  data-testid="export-json-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  JSON
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {Object.entries(categories).map(([categoryId, category]) => {
              const CategoryIcon = category.icon;
              const categoryPages = Object.entries(translationRegistry).filter(([_, page]) => page.category === categoryId);
              const isExpanded = expandedCategories.includes(categoryId);
              
              // Calculate category stats
              let categoryTotal = 0;
              let categoryTranslated = 0;
              categoryPages.forEach(([pageId]) => {
                const pageStats = translationStats[selectedLanguage]?.pages[pageId];
                if (pageStats) {
                  categoryTotal += pageStats.total;
                  categoryTranslated += pageStats.translated;
                }
              });
              const categoryPercentage = categoryTotal > 0 ? Math.round((categoryTranslated / categoryTotal) * 100) : 0;

              return (
                <div key={categoryId} className="mb-4">
                  <button
                    onClick={() => toggleCategory(categoryId)}
                    className="w-full flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                    data-testid={`category-${categoryId}`}
                  >
                    <div className="flex items-center gap-3">
                      <CategoryIcon className="w-5 h-5 text-muted-foreground" />
                      <span className="font-medium">{category.name}</span>
                      <Badge variant="outline" className="ml-2">
                        {categoryPages.length} {t('translationDashboard.pages', 'pages')}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-32">
                        <Progress value={categoryPercentage} className="h-2" />
                      </div>
                      <span className="text-sm text-muted-foreground w-12">{categoryPercentage}%</span>
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <div className="mt-2 ml-8 space-y-2">
                      {categoryPages.map(([pageId, page]) => {
                        const pageStats = translationStats[selectedLanguage]?.pages[pageId];
                        const pagePercentage = pageStats ? Math.round((pageStats.translated / pageStats.total) * 100) : 0;
                        
                        return (
                          <div 
                            key={pageId} 
                            className="flex items-center justify-between p-2 bg-background rounded border"
                            data-testid={`page-${pageId}`}
                          >
                            <div className="flex items-center gap-2">
                              {pagePercentage === 100 ? (
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                              ) : (
                                <AlertCircle className="w-4 h-4 text-amber-500" />
                              )}
                              <span className="text-sm">{page.name}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-muted-foreground">
                                {pageStats?.translated}/{pageStats?.total}
                              </span>
                              <div className="w-20">
                                <Progress value={pagePercentage} className="h-1.5" />
                              </div>
                              <Badge 
                                variant={pagePercentage === 100 ? 'default' : 'outline'}
                                className="w-12 justify-center"
                              >
                                {pagePercentage}%
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-4xl font-bold text-primary">{Object.keys(translationRegistry).length}</p>
                <p className="text-sm text-muted-foreground mt-1">{t('translationDashboard.totalPages', 'Total Pages/Components')}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-4xl font-bold text-green-500">
                  {Object.values(languageInfo).filter((_, i) => overallPercentage(Object.keys(languageInfo)[i]) >= 90).length}
                </p>
                <p className="text-sm text-muted-foreground mt-1">{t('translationDashboard.languagesComplete', 'Languages 90%+ Complete')}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-4xl font-bold text-amber-500">
                  {translationStats[selectedLanguage]?.total - translationStats[selectedLanguage]?.translated}
                </p>
                <p className="text-sm text-muted-foreground mt-1">{t('translationDashboard.missingKeys', 'Missing Keys')} ({languageInfo[selectedLanguage]?.name})</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
